import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useLocaleFmt } from "@/lib/i18n/format";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Play, RotateCcw, AlertTriangle, CheckCircle, Clock, XCircle } from "lucide-react";
import {
  listAutomationRuns, automationStats, retryAutomationRun, triggerAutomationsRun,
} from "@/lib/automations.functions";

export const Route = createFileRoute("/_authenticated/app/admin/automations")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase
      .from("user_roles").select("role").eq("user_id", u.user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: Page,
});

function Page() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [stats, setStats] = useState<{ active: number; completed: number; dlq: number; failed24h: number } | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [status, setStatus] = useState<"all" | "active" | "completed" | "stopped" | "dlq">("all");
  const [busy, setBusy] = useState(false);

  const listFn = useServerFn(listAutomationRuns);
  const statsFn = useServerFn(automationStats);
  const retryFn = useServerFn(retryAutomationRun);
  const triggerFn = useServerFn(triggerAutomationsRun);

  const reload = async () => {
    const [s, l] = await Promise.all([statsFn({}), listFn({ data: { status, limit: 200 } })]);
    if (s.ok) setStats({ active: s.active, completed: s.completed, dlq: s.dlq, failed24h: s.failed24h });
    if (l.ok) setItems(l.items);
  };

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [status]);

  const triggerNow = async () => {
    setBusy(true);
    const r = await triggerFn({});
    setBusy(false);
    if (!r.ok) return toast.error((r as any).error ?? t("pages.admin.genericError"));
    toast.success(t("pages.admin.automationsPage.runDone", { n: (r as any).body?.processed ?? 0 }));
    reload();
  };

  const retry = async (id: string) => {
    const r = await retryFn({ data: { id } });
    if (!r.ok) return toast.error(r.error ?? t("pages.admin.genericError"));
    toast.success(t("pages.admin.retryScheduled"));
    reload();
  };

  return (
    <>
      <PageHeader
        title={t("pages.admin.automationsPage.title")}
        description={t("pages.admin.automationsPage.description")}
        actions={
          <Button onClick={triggerNow} disabled={busy} size="sm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
            {t("pages.admin.automationsPage.runNow")}
          </Button>
        }
      />
      <PageBody>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          {[
            { label: t("pages.admin.automationsPage.active"), value: stats?.active ?? 0, icon: Clock, tone: "text-emerald-400" },
            { label: t("pages.admin.automationsPage.completed"), value: stats?.completed ?? 0, icon: CheckCircle, tone: "text-blue-400" },
            { label: t("pages.admin.automationsPage.failed24h"), value: stats?.failed24h ?? 0, icon: AlertTriangle, tone: "text-amber-400" },
            { label: t("pages.admin.automationsPage.deadLetter"), value: stats?.dlq ?? 0, icon: XCircle, tone: "text-destructive" },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="flex items-center gap-3 pt-6">
                <s.icon className={`h-5 w-5 ${s.tone}`} />
                <div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mb-3 flex items-center justify-between gap-3">
          <Select value={status} onValueChange={(v) => setStatus(v as any)}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.admin.automationsPage.all")}</SelectItem>
              <SelectItem value="active">{t("pages.admin.automationsPage.active")}</SelectItem>
              <SelectItem value="completed">{t("pages.admin.automationsPage.completed")}</SelectItem>
              <SelectItem value="stopped">{t("pages.admin.automationsPage.stopped")}</SelectItem>
              <SelectItem value="dlq">{t("pages.admin.automationsPage.dlq")}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={reload}>{t("pages.admin.automationsPage.update")}</Button>
        </div>

        <div className="overflow-hidden rounded-2xl border">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t("pages.admin.cols.sequence")}</th>
                <th className="px-3 py-2">{t("pages.admin.cols.email")}</th>
                <th className="px-3 py-2">{t("pages.admin.cols.step")}</th>
                <th className="px-3 py-2">{t("pages.admin.cols.status")}</th>
                <th className="px-3 py-2">{t("pages.admin.cols.attempts")}</th>
                <th className="px-3 py-2">{t("pages.admin.cols.next")}</th>
                <th className="px-3 py-2">{t("pages.admin.cols.lastError")}</th>
                <th className="px-3 py-2 text-right">{t("pages.admin.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 font-medium">{r.sequence}</td>
                  <td className="px-3 py-2 text-xs">{r.email}</td>
                  <td className="px-3 py-2">{r.current_step}</td>
                  <td className="px-3 py-2">
                    <Badge variant={r.status === "dlq" ? "destructive" : r.status === "active" ? "default" : "secondary"}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">{r.attempts ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.next_run_at ? new Date(r.next_run_at).toLocaleString(localeTag) : "-"}
                  </td>
                  <td className="px-3 py-2 max-w-[240px] truncate text-xs text-destructive" title={r.last_error ?? ""}>
                    {r.last_error ?? "-"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {(r.status === "dlq" || (r.attempts ?? 0) > 0) && (
                      <Button size="sm" variant="ghost" onClick={() => retry(r.id)}>
                        <RotateCcw className="mr-1 h-3 w-3" /> {t("pages.admin.retry")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">{t("pages.admin.automationsPage.noRuns")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}
